import { useEffect, useState } from 'react';
import { useLang } from '../../../i18n';
import { useToast } from '../../../components/Toast';
import { get, post, del } from '../../../services/api';
import Card from '../../../components/Card';
import Button from '../../../components/Button';
import Switch from '../../../components/Switch';
import Skeleton from '../../../components/Skeleton';

interface Session {
  public_id: string;
  user_agent: string;
  ip_address: string;
  last_active_at: string;
}

interface TfaState { enabled: boolean }

export default function ProfileSecurity() {
  const { t } = useLang();
  const toast = useToast();
  const [tfa, setTfa] = useState<TfaState | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tfaRes, sesRes] = await Promise.all([
          get<TfaState>('/me/2fa/').catch(() => ({ enabled: false })),
          get<{ results: Session[] }>('/me/sessions/').catch(() => ({ results: [] })),
        ]);
        if (cancelled) return;
        setTfa(tfaRes);
        setSessions(sesRes.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleTfa = async (enabled: boolean) => {
    try {
      if (enabled) await post('/me/2fa/enable/');
      else await post('/me/2fa/disable/');
      setTfa({ enabled });
      toast.success(t('profile.saved'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const terminateAll = async () => {
    try {
      await del('/me/sessions/');
      setSessions([]);
      toast.success(t('profile.saved'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  if (loading) return <Skeleton height={200} />;

  return (
    <div className="profile-security">
      <Card padding="md">
        <div className="profile-security__row">
          <div>
            <div className="profile-security__label">{t('profile.security.2fa')}</div>
            <p className="profile-security__note">{t('profile.security.passwordless_note')}</p>
          </div>
          <Switch checked={!!tfa?.enabled} onChange={(e) => toggleTfa(e.target.checked)} />
        </div>
      </Card>
      <Card padding="md">
        <div className="profile-security__row">
          <div className="profile-security__label">{t('profile.security.sessions')}</div>
          <Button variant="secondary" size="sm" onClick={terminateAll}>{t('profile.security.terminate_all')}</Button>
        </div>
        <hr className="profile-security__divider" />
        {sessions.length === 0 ? (
          <p className="profile-security__note">—</p>
        ) : (
          <ul className="profile-security__sessions">
            {sessions.map((s) => (
              <li key={s.public_id} className="profile-security__session">
                <div>
                  <div className="profile-security__label">{s.user_agent}</div>
                  <p className="profile-security__note">{s.ip_address} • {new Date(s.last_active_at).toLocaleString()}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
